<template>
  <div class="match-details-page">
    <MainNavbar />
    <!-- Main Content -->
    <main class="details-main">
      <div class="details-container">
        <!-- Back Button -->
        <button class="back-button" @click="$router.back()">
          <i class="fa-solid fa-arrow-left"></i>
          Back
        </button>

        <!-- Match Header -->
        <div class="match-header" v-if="match">
          <div class="header-content">
            <div class="match-id-section">
              <i class="fa-solid fa-futbol"></i>
              <h1>Match #{{ match.id || match.match_id }}</h1>
            </div>
            <span class="status-badge" :class="match.status">
              {{ formatMatchStatus(match.status) }}
            </span>
          </div>
        </div>

        <!-- Match Content -->
        <div v-if="match" class="match-content">
          <!-- Match Information Card -->
          <div class="details-card">
            <div class="card-header">
              <h2>
                <i class="fa-solid fa-info-circle"></i>
                Match Information
              </h2>
            </div>
            <div class="card-body">
              <div class="details-grid">
                <div class="detail-item">
                  <span class="detail-label">
                    <i class="fa-solid fa-calendar"></i>
                    Date
                  </span>
                  <span class="detail-value">{{ formatDate(match.date || match.scheduled_at) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">
                    <i class="fa-solid fa-clock"></i>
                    Time
                  </span>
                  <span class="detail-value">{{ formatTime(match.date || match.scheduled_at) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">
                    <i class="fa-solid fa-hourglass-half"></i>
                    Duration
                  </span>
                  <span class="detail-value">{{ formatDuration(match.duration || match.duration_minutes) }}</span>
                </div>
                <div class="detail-item" v-if="match.match_type">
                  <span class="detail-label">
                    <i class="fa-solid fa-tag"></i>
                    Match Type
                  </span>
                  <span class="detail-value">{{ capitalizeText(match.match_type) }}</span>
                </div>
                <div class="detail-item" v-if="match.match_format">
                  <span class="detail-label">
                    <i class="fa-solid fa-users"></i>
                    Format
                  </span>
                  <span class="detail-value">{{ match.match_format }}</span>
                </div>
                <div class="detail-item" v-if="match.max_players">
                  <span class="detail-label">
                    <i class="fa-solid fa-user-group"></i>
                    Max Players
                  </span>
                  <span class="detail-value">{{ match.max_players }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Scores Card -->
          <div class="details-card" v-if="match.home_score !== null || match.away_score !== null">
            <div class="card-header">
              <h2>
                <i class="fa-solid fa-trophy"></i>
                Score
              </h2>
            </div>
            <div class="card-body">
              <div class="score-display">
                <div class="score-team">
                  <span class="score-label">Home</span>
                  <span class="score-value">{{ match.home_score ?? '-' }}</span>
                </div>
                <span class="score-separator">:</span>
                <div class="score-team">
                  <span class="score-label">Away</span>
                  <span class="score-value">{{ match.away_score ?? '-' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Venue Card -->
          <div class="details-card" v-if="match.venue">
            <div class="card-header">
              <h2>
                <i class="fa-solid fa-map-marker-alt"></i>
                Venue
              </h2>
            </div>
            <div class="card-body">
              <div class="details-grid">
                <div class="detail-item">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">{{ match.venue.name || 'TBD' }}</span>
                </div>
                <div class="detail-item" v-if="match.venue.city">
                  <span class="detail-label">City</span>
                  <span class="detail-value">{{ match.venue.city }}</span>
                </div>
                <div class="detail-item" v-if="match.venue.address">
                  <span class="detail-label">Address</span>
                  <span class="detail-value">{{ match.venue.address }}</span>
                </div>
                <div class="detail-item" v-if="match.venue.notes">
                  <span class="detail-label">
                    <i class="fa-solid fa-map-location-dot"></i>
                    Location
                  </span>
                  <a 
                    :href="match.venue.notes" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="location-link"
                  >
                    <i class="fa-solid fa-external-link"></i>
                    View on Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Description Card -->
          <div class="details-card" v-if="match.description">
            <div class="card-header">
              <h2>
                <i class="fa-solid fa-file-lines"></i>
                Description
              </h2>
            </div>
            <div class="card-body">
              <p class="description-text">{{ match.description }}</p>
            </div>
          </div>

          <!-- Opponent Team Card -->
          <div class="details-card">
            <div class="card-header">
              <h2>
                <i class="fa-solid fa-users"></i>
                Opponent Team
              </h2>
            </div>
            <div class="card-body">
              <div v-if="loadingOpponent" class="loading-section">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>Loading opponent team details...</span>
              </div>
              <div v-else-if="opponentTeam" class="opponent-team-section">
                <div class="team-header-info">
                  <div v-if="opponentTeam.logo_url" class="team-logo">
                    <img
                      :src="opponentTeam.logo_url"
                      :alt="opponentTeam.name"
                      loading="lazy"
                      decoding="async"
                      @error="handleImageError"
                    >
                  </div>
                  <div v-else class="team-logo-placeholder">
                    <i class="fa-solid fa-users"></i>
                  </div>
                  <div class="team-info">
                    <h3>{{ opponentTeam.name || match.opponent_name }}</h3>
                    <p v-if="opponentTeam.team_type" class="team-type">{{ opponentTeam.team_type }}</p>
                  </div>
                </div>
                <div class="details-grid">
                  <div class="detail-item" v-if="opponentTeam.skill_level">
                    <span class="detail-label">Skill Level</span>
                    <span class="detail-value">
                      <span class="skill-badge" :class="opponentTeam.skill_level?.toLowerCase()">
                        {{ opponentTeam.skill_level }}
                      </span>
                    </span>
                  </div>
                  <div class="detail-item" v-if="opponentTeam.county || opponentTeam.constituency">
                    <span class="detail-label">Location</span>
                    <span class="detail-value">
                      {{ [opponentTeam.county, opponentTeam.constituency].filter(Boolean).join(', ') }}
                    </span>
                  </div>
                  <div class="detail-item" v-if="opponentTeam.total_matches !== undefined">
                    <span class="detail-label">Total Matches</span>
                    <span class="detail-value">{{ opponentTeam.total_matches || 0 }}</span>
                  </div>
                  <div class="detail-item" v-if="opponentTeam.wins !== undefined">
                    <span class="detail-label">Wins</span>
                    <span class="detail-value">{{ opponentTeam.wins || 0 }}</span>
                  </div>
                  <div class="detail-item" v-if="opponentTeam.losses !== undefined">
                    <span class="detail-label">Losses</span>
                    <span class="detail-value">{{ opponentTeam.losses || 0 }}</span>
                  </div>
                  <div class="detail-item" v-if="opponentTeam.member_count !== undefined">
                    <span class="detail-label">Players</span>
                    <span class="detail-value">{{ opponentTeam.member_count || 0 }}</span>
                  </div>
                  <div class="detail-item" v-if="opponentTeam.home_ground">
                    <span class="detail-label">Home Ground</span>
                    <span class="detail-value">{{ opponentTeam.home_ground }}</span>
                  </div>
                  <div class="detail-item" v-if="opponentTeam.phone">
                    <span class="detail-label">
                      <i class="fa-solid fa-phone"></i>
                      Phone
                    </span>
                    <span class="detail-value">
                      <a :href="`tel:${opponentTeam.phone}`" class="opponent-phone-link">{{ opponentTeam.phone }}</a>
                    </span>
                  </div>
                </div>
                <p v-if="opponentTeam.bio" class="team-bio">{{ opponentTeam.bio }}</p>
              </div>
              <div v-else class="no-team-info">
                <i class="fa-solid fa-info-circle"></i>
                <p>{{ match.opponent_name || 'Opponent team information not available' }}</p>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div
            class="action-buttons"
            v-if="['confirmed', 'scheduled'].includes((match.status || '').toLowerCase())"
          >
            <button class="btn-action btn-primary" @click="viewTactics">
              <i class="fa-solid fa-chess-board"></i>
              View Tactics and Formations
            </button>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <i class="fa-solid fa-exclamation-triangle"></i>
          <h3>Error Loading Match</h3>
          <p>{{ error }}</p>
          <button class="btn-action btn-secondary" @click="loadMatchDetails">
            <i class="fa-solid fa-refresh"></i>
            Try Again
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: 'MatchDetails',
  loadingMessage: 'Loading match details...',
  mixins: [globalLoadingMixin],
  components: {
    MainNavbar,
  },
  data() {
    return {
      loading: true,
      loadingOpponent: false,
      match: null,
      opponentTeam: null,
      error: null
    };
  },
  async mounted() {
    await this.loadMatchDetails();
  },
  methods: {
    async loadMatchDetails() {
      this.loading = true;
      this.error = null;
      
      try {
        const matchId = this.$route.params.match_id || this.$route.params.id;
        
        if (!matchId) {
          throw new Error('Match ID not provided');
        }

        const token = this.getAuthToken();
        if (!token) {
          throw new Error('Authentication required');
        }

        // Fetch match details from team matches endpoint
        // We'll need to get it from the team's matches list
        const teamId = this.$route.query.team_id;
        if (!teamId) {
          throw new Error('Team ID is required');
        }

        const response = await fetch(buildApiUrl(`/api/matches/teams/${teamId}/matches`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch matches: ${response.status}`);
        }

        const data = await response.json();
        const matches = data.matches || [];
        
        // Find the specific match
        const foundMatch = matches.find(m => 
          String(m.id) === String(matchId) || 
          String(m.match_id) === String(matchId)
        );

        if (!foundMatch) {
          throw new Error('Match not found');
        }

        this.match = foundMatch;

        // Fetch opponent team details
        if (this.match.opponent_team_id) {
          await this.fetchOpponentTeam(this.match.opponent_team_id);
        }
      } catch (error) {
        console.error('Error loading match details:', error);
        this.error = error.message || 'Failed to load match details';
      } finally {
        this.loading = false;
      }
    },
    async fetchOpponentTeam(teamId) {
      this.loadingOpponent = true;
      try {
        const token = this.getAuthToken();
        if (!token) {
          return;
        }
        
        const response = await fetch(buildApiUrl(`/api/teams/${teamId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          const teamData = await response.json();
          this.opponentTeam = {
            ...teamData,
            phone: teamData.phone || teamData.team_phone || null,
          };
        } else {
          console.warn('Failed to fetch opponent team:', response.status);
        }
      } catch (error) {
        console.error('Error fetching opponent team:', error);
      } finally {
        this.loadingOpponent = false;
      }
    },
    getAuthToken() {
      return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token')
      );
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    },
    formatDuration(duration) {
      if (!duration) {
        return '90 minutes';
      }
      
      if (typeof duration === 'string' && (duration.includes('mins') || duration.includes('minutes'))) {
        return duration;
      }
      
      const numDuration = parseInt(duration, 10);
      if (!isNaN(numDuration)) {
        return `${numDuration} minutes`;
      }
      
      return duration || '90 minutes';
    },
    formatMatchStatus(status) {
      if (!status) return 'Unknown';
      const statusMap = {
        'pending': 'Pending',
        'confirmed': 'Confirmed',
        'scheduled': 'Scheduled',
        'past': 'Past',
        'completed': 'Completed',
        'cancelled': 'Cancelled',
        'postponed': 'Postponed'
      };
      return statusMap[status.toLowerCase()] || status;
    },
    capitalizeText(text) {
      if (!text || typeof text !== 'string') return text;
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.style.display = 'none';
        const placeholder = event.target.parentElement.querySelector('.team-logo-placeholder');
        if (placeholder) {
          placeholder.style.display = 'flex';
        }
      }
    },
    viewTactics() {
      if (this.match && this.$route.query.team_id) {
        this.$router.push(`/ipromiseafrica/matchup/tactics/${this.$route.query.team_id}/match/${this.match.id || this.match.match_id}`);
      }
    }
  }
};
</script>

<style scoped>
.match-details-page {
  background: var(--dashboard-bg);
  min-height: 100vh;
  padding-top: 120px;
}

.details-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.details-container {
  position: relative;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: var(--subtext-color);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
  transform: translateX(-5px);
}

.match-header {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px 30px;
  margin-bottom: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.match-id-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.match-id-section i {
  font-size: 2rem;
  color: var(--section-icon);
}

.match-id-section h1 {
  margin: 0;
  color: var(--heading-color);
  font-size: 2rem;
  font-weight: 800;
}

.status-badge {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
}

.status-badge.pending {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.status-badge.confirmed {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.status-badge.scheduled {
  background: rgba(59, 130, 246, 0.15);
  color: #93c5fd;
  border: 1px solid rgba(59, 130, 246, 0.35);
}

.status-badge.past {
  background: rgba(148, 163, 184, 0.2);
  color: #cbd5e1;
  border: 1px solid rgba(148, 163, 184, 0.35);
}

.status-badge.completed {
  background: var(--action-success-bg);
  color: var(--action-success-color);
  border: 1px solid var(--action-success-color);
}

.status-badge.cancelled {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.match-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.details-card {
  background: var(--card-bg);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header {
  padding: 20px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.3rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header h2 i {
  color: var(--section-icon);
  font-size: 1.1rem;
}

.card-body {
  padding: 24px 30px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-label {
  font-size: 0.85rem;
  color: var(--subtext-color);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-label i {
  color: var(--section-icon);
  font-size: 0.9rem;
}

.detail-value {
  font-size: 1rem;
  color: var(--heading-color);
  font-weight: 600;
}

.location-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--action-primary-color);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 8px 16px;
  background: rgba(58, 134, 255, 0.1);
  border: 1px solid rgba(58, 134, 255, 0.3);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.location-link:hover {
  background: rgba(58, 134, 255, 0.2);
  border-color: var(--action-primary-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(58, 134, 255, 0.3);
}

.location-link i {
  font-size: 0.85rem;
}

.opponent-phone-link {
  color: var(--action-primary-color);
  text-decoration: none;
  font-weight: 600;
}

.opponent-phone-link:hover {
  text-decoration: underline;
}

.score-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  padding: 30px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.score-team {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.score-label {
  font-size: 0.9rem;
  color: var(--subtext-color);
  font-weight: 500;
}

.score-value {
  font-size: 3rem;
  font-weight: 800;
  color: var(--heading-color);
}

.score-separator {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--subtext-color);
}

.description-text {
  color: var(--subtext-color);
  line-height: 1.8;
  margin: 0;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  font-size: 0.95rem;
}

.loading-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: var(--subtext-color);
}

.loading-section i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.opponent-team-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.team-header-info {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.team-logo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--card-hover-border);
  flex-shrink: 0;
}

.team-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.team-logo-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--meta-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--card-hover-border);
  color: var(--section-icon);
  font-size: 2rem;
  flex-shrink: 0;
}

.team-info h3 {
  margin: 0 0 8px 0;
  color: var(--heading-color);
  font-size: 1.5rem;
  font-weight: 700;
}

.team-info .team-type {
  margin: 0;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.team-bio {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  line-height: 1.8;
  font-size: 0.95rem;
}

.skill-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.skill-badge.beginner {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.skill-badge.intermediate {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.skill-badge.advanced {
  background: linear-gradient(135deg, #9C27B0, #7B1FA2);
  color: white;
}

.no-team-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;
  color: var(--subtext-color);
  text-align: center;
}

.no-team-info i {
  font-size: 2rem;
  color: var(--section-icon);
}

.action-buttons {
  display: flex;
  gap: 16px;
  margin-top: 8px;
}

.btn-action {
  padding: 14px 28px;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.btn-primary {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.btn-primary:hover {
  background: var(--action-primary-bg);
  opacity: 0.9;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid var(--border-color-hover);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: var(--card-bg);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.loading-state i,
.error-state i {
  font-size: 3rem;
  color: var(--section-icon);
  margin-bottom: 20px;
}

.error-state i {
  color: var(--rejected-color);
}

.loading-state p,
.error-state p {
  color: var(--subtext-color);
  margin: 10px 0 20px 0;
}

.error-state h3 {
  color: var(--heading-color);
  margin: 0 0 10px 0;
}

@media (max-width: 768px) {
  .details-main {
    padding: 16px;
  }

  .match-header {
    padding: 20px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .match-id-section h1 {
    font-size: 1.5rem;
  }

  .card-body {
    padding: 20px;
  }

  .details-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .score-display {
    flex-direction: column;
    gap: 15px;
  }

  .score-separator {
    transform: rotate(90deg);
  }

  .team-header-info {
    flex-direction: column;
    text-align: center;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn-action {
    width: 100%;
  }
}
</style>
